import React, { useState, useMemo, useRef } from 'react';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import DiamondTwoToneIcon from '@mui/icons-material/DiamondTwoTone';
import TerrainIcon from '@mui/icons-material/Terrain';
import StyleIcon from '@mui/icons-material/Style';
import { 
  IconButton, 
  Badge, 
  Popover,
  Box, 
  Typography, 
  Button, 
  List, 
  ListItem, 
  ListItemText,
  Divider, 
  CircularProgress,
  Paper,
  Tooltip,
  Avatar,
  useTheme
} from '@mui/material';
import { formatPriceToDec, formatPriceToUsd, formatPriceToCredits, convertUsdToDec, convertUsdToCredits } from '../../scenes/market/priceUtils';
import { useCart } from '../../contexts/CartContext';
import { useLandCart } from '../../contexts/LandCartContext';
import { useItemCart } from '../../contexts/ItemCartContext';
import { tokens } from "../../theme";
import { CARD_FOILS } from '../../constants/filterConstants';
import DiamondAnimation from '../animations/DiamondAnimation';
import priceService from '../../services/PriceService';

const ShoppingCard = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const { cartItems, removeFromCart, buyItem, buyItems, clearCart, loading } = useCart();
  const { landCartItems, removeFromLandCart, buyLandDeed, buyLandDeeds, clearLandCart, loading: landLoading } = useLandCart();
  const { itemCartItems, removeFromItemCart, buyItems: buyItemCartEntries, clearItemCart, loading: itemLoading } = useItemCart();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  // Animation states
  const [animationTrigger, setAnimationTrigger] = useState(0);
  const cartRef = useRef(null);

  // Calculate total cart items (cards + land deeds + market items)
  const totalCartItems = useMemo(() => cartItems.length + landCartItems.length + itemCartItems.length, [cartItems, landCartItems, itemCartItems]);
  
  // Get target element (cashback display or wallet in topbar)
  const getCashbackElement = () => {
    console.log('🎯 Looking for target element...');
    
    // Try to find cashback display first (if on market page)
    const cashbackElement = document.querySelector('[data-cashback-target="true"]');
    console.log('🎯 Cashback element found:', cashbackElement);
    if (cashbackElement) {
      const rect = cashbackElement.getBoundingClientRect();
      console.log('Cashback element position:', rect);
      return cashbackElement;
    }
    
    // Fallback to wallet/account element in topbar (same as cashback animation)
    const walletSelectors = [
      '[data-wallet-target="true"]',
      '[aria-controls="account-menu"]', // Account button that opens menu
      '.MuiAvatar-root', // Avatar Element
      '[aria-label*="account"]', // Account Button
    ];
    
    for (const selector of walletSelectors) {
      const element = document.querySelector(selector);
      console.log(`🔍 Trying selector "${selector}":`, element);
      if (element) {
        const rect = element.getBoundingClientRect();
        console.log(`Found element position:`, rect);
        return element;
      }
    }
    
    // Final fallback: any visible avatar element
    const avatars = document.querySelectorAll('.MuiAvatar-root');
    console.log('🔄 Avatar elements found:', avatars.length);
    for (const avatar of avatars) {
      const rect = avatar.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        console.log('🔄 Using visible avatar:', avatar, rect);
        return avatar;
      }
    }
    
    console.log('❌ No suitable target element found');
    return null;
  };
  
  // Trigger animation function
  const triggerShoppingAnimation = () => {
    console.log('🛒 Triggering shopping animation...');
    console.log('Cart items:', cartItems.length);
    console.log('Source element (cart):', cartRef.current);
    
    // Call getCashbackElement at animation time, not at render time
    const targetElement = getCashbackElement();
    console.log('Target element (cashback):', targetElement);
    
    if (!cartRef.current) {
      console.error('❌ Cart reference not found');
      return;
    }
    
    if (!targetElement) {
      console.error('❌ Target element not found');
      return;
    }
    
    // Check if elements have valid positions
    const cartRect = cartRef.current.getBoundingClientRect();
    const targetRect = targetElement.getBoundingClientRect();
    
    console.log('Cart position:', cartRect);
    console.log('Target position:', targetRect);
    
    if (cartRect.width === 0 || targetRect.width === 0) {
      console.error('❌ Elements have invalid dimensions');
      return;
    }
    
    console.log('✅ Animation will start...');
    setAnimationTrigger(prev => prev + 1);
  };

  // Calculate totals for the cart
  const totals = useMemo(() => {
    let totalUsd = 0;
    let totalDec = 0;
    let totalCredits = 0;
    let totalBcx = 0; // Add BCX tracking

    // Process regular cart items (cards)
    cartItems.forEach(item => {
      // Skip land deeds - they're now handled in landCartItems
      if (item.type === 'land_deed') return;
      
      // Add BCX to total (cards only, not land deeds)
      const bcx = parseFloat(item.bcx || 0);
      totalBcx += bcx;
      
      if (item.originalCurrency === 'USD') {
        // USD-priced item
        const usdPrice = parseFloat(item.originalPrice || item.price || item.buy_price || 0);
        totalUsd += usdPrice;
        totalDec += convertUsdToDec(usdPrice);
        totalCredits += convertUsdToCredits(usdPrice);
      } else {
        // Card priced in DEC
        const decPrice = parseFloat(item.price || item.buy_price || 0);
        totalDec += decPrice;
        // Convert DEC back to USD for display (using current DEC price)
        const { prices } = priceService.getData();
        const decPriceUsd = prices?.dec || 0.001; // Fallback price
        const usdValue = decPrice * decPriceUsd;
        totalUsd += usdValue;
        totalCredits += convertUsdToCredits(usdValue);
      }
    });
    
    // Process land cart items
    landCartItems.forEach(item => {
      // All land deeds are priced in USD
      const usdPrice = parseFloat(item.price || item.buy_price || item.originalPrice || item.listing_price || 0);
      totalUsd += usdPrice;
      totalDec += convertUsdToDec(usdPrice);
      totalCredits += convertUsdToCredits(usdPrice);
      // Note: Land deeds don't have BCX, so we don't add to totalBcx
    });

    // Process market item cart entries (packs, totems, resources, ...) - totalCost is USD
    itemCartItems.forEach(cartItem => {
      const usdPrice = parseFloat(cartItem.totalCost || 0);
      totalUsd += usdPrice;
      totalDec += convertUsdToDec(usdPrice);
      totalCredits += convertUsdToCredits(usdPrice);
    });

    return {
      usdTotal: totalUsd,
      decTotal: totalDec,
      creditsTotal: totalCredits,
      bcxTotal: totalBcx // Add BCX total to return
    };
  }, [cartItems, landCartItems, itemCartItems]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // Helper function to get foil status display
  const getFoilDisplay = (item) => {
    // Check if item has foil property, otherwise check gold property for backward compatibility
    const foilType = item.foil !== undefined ? item.foil : (item.gold ? 1 : 0);
    const foilData = CARD_FOILS.find(f => f.id === foilType) || CARD_FOILS[0]; // Default to Regular
    
    // Don't show anything for regular cards
    if (foilType === 0) return null;
    
    const getFoilAbbreviation = (name) => {
      switch(name) {
        case 'Gold Foil': return 'GF';
        case 'Gold Arcana': return 'GA';
        case 'Black Foil': return 'BF';
        case 'Black Arcana': return 'BA';
        default: return 'R';
      }
    };

    return (
      <Tooltip title={foilData.name}>
        <Avatar 
          sx={{ 
            width: 16, 
            height: 16, 
            ml: 0.5, 
            bgcolor: foilData.color,
            color: foilType === 1 || foilType === 2 ? 'black' : 'white', // Gold foils get black text, others white
            fontSize: '8px',
            fontWeight: 'bold',
            border: foilType === 3 || foilType === 4 ? '1px solid #666' : 'none' // Add border for black foils for visibility
          }}
        >
          {getFoilAbbreviation(foilData.name)}
        </Avatar>
      </Tooltip>
    );
  };

  // Handler to remove an item from cart
  const handleRemoveItem = (uid) => {
    removeFromCart(uid);
  };
  
  // Handler to buy an item
  const handleBuyItem = async (item, currency) => {
    console.log('🛒 Attempting to buy single item:', { item, currency });
    try {
      const result = await buyItem(item, currency);
      console.log('✅ Purchase result:', result);
      
      // Show result message to user (including warnings about removed items)
      if (result && result.message) {
        alert(result.message);
      }
      
      // Trigger animation on successful purchase
      if (result && result.success !== false) {
        setTimeout(() => {
          triggerShoppingAnimation();
        }, 300); // Small delay to ensure purchase is visible
      }
    } catch (error) {
      console.error('❌ Error purchasing item:', error);
      // Show error message to user
      alert(`Purchase failed: ${error.message}`);
    }
  };

  // Handler to buy all items in cart with bulk purchase
  const handleBuyAllItems = async (currency) => {
    console.log('🛍️ Attempting to buy all items:', { cartItems, currency });
    try {
      const result = await buyItems(cartItems, currency);
      console.log('✅ Bulk purchase result:', result);
      
      // Show result message to user (including warnings about removed items)
      if (result && result.message) {
        alert(result.message);
      }
      
      // Trigger animation on successful purchase
      if (result && result.success !== false) {
        setTimeout(() => {
          triggerShoppingAnimation();
        }, 300); // Small delay to ensure purchase is visible
      }
    } catch (error) {
      console.error('❌ Error purchasing all items:', error);
      // Show error message to user
      alert(`Purchase failed: ${error.message}`);
    }
  };
  
  // Handler to remove a land deed from cart
  const handleRemoveLandItem = (uid) => {
    removeFromLandCart(uid);
  };

  // Handler to buy a single land deed
  const handleBuyLandItem = async (item, currency) => {
    console.log('🏞️ Attempting to buy land deed:', { item, currency });
    try {
      const result = await buyLandDeed(item, currency);
      console.log('✅ Land purchase result:', result);
      
      // Trigger animation on successful purchase
      if (result && result.success !== false) {
        setTimeout(() => {
          triggerShoppingAnimation();
        }, 300); // Small delay to ensure purchase is visible
      }
    } catch (error) {
      console.error('❌ Error purchasing land deed:', error);
      // Show error message to user
      alert(`Land deed purchase failed: ${error.message}`);
    }
  };

  // Handler to buy all land deeds in cart
  const handleBuyAllLandItems = async (currency) => {
    console.log('🏞️ Attempting to buy all land deeds:', { landCartItems, currency });
    try {
      const result = await buyLandDeeds(landCartItems, currency);
      console.log('✅ Bulk land purchase result:', result);
      
      // Trigger animation on successful purchase
      if (result && result.success !== false) {
        setTimeout(() => {
          triggerShoppingAnimation();
        }, 300); // Small delay to ensure purchase is visible
      }
    } catch (error) {
      console.error('❌ Error purchasing all land deeds:', error);
      // Show error message to user
      alert(`Land deed purchases failed: ${error.message}`);
    }
  };

  // Handler to remove a market item entry from cart
  const handleRemoveItemCartEntry = (cartItemId) => {
    removeFromItemCart(cartItemId);
  };

  // Handler to buy all market items in cart
  const handleBuyAllItemCartEntries = async (currency) => {
    try {
      const result = await buyItemCartEntries(itemCartItems, currency);
      if (result && result.success !== false) {
        setTimeout(() => {
          triggerShoppingAnimation();
        }, 300);
      }
    } catch (error) {
      console.error('❌ Error purchasing all items:', error);
      alert(`Purchase failed: ${error.message}`);
    }
  };

  const open = Boolean(anchorEl);
  const id = open ? 'shopping-cart-popover' : undefined;

  return (
    <React.Fragment>
      <Tooltip title="Shopping Cart">
        <IconButton 
          ref={cartRef}
          color="inherit" 
          onClick={handleClick} 
          aria-label="Shopping Cart"
        >
          <Badge 
            badgeContent={totalCartItems} 
            color="secondary"
            sx={{
              '& .MuiBadge-badge': {
                backgroundColor: colors.greenAccent[500],
                color: 'black',
                fontWeight: 'bold'
              }
            }}
          >
            <ShoppingCartIcon sx={{ color: colors.greenAccent[400] }} />
          </Badge>
        </IconButton>
      </Tooltip>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            width: 400,
            maxHeight: 600,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            bgcolor: theme.palette.mode === 'dark' ? colors.primary[400] : 'background.paper',
            borderRadius: 1,
            boxShadow: 3
          }
        }}
      >
        <Box sx={{ p: 2, borderBottom: `1px solid ${colors.grey[500]}`, flexShrink: 0 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center">
              <ShoppingCartIcon sx={{ mr: 1, color: colors.greenAccent[500] }} />
              <Typography variant="h6" fontWeight="bold">
                Shopping Cart {(loading || landLoading) && <CircularProgress size={16} sx={{ ml: 1, verticalAlign: 'middle' }} />}
              </Typography>
              <Typography variant="body2" color={colors.grey[500]} sx={{ ml: 1 }}>
                ({totalCartItems})
              </Typography>
            </Box>
            <IconButton size="small" onClick={handleClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
        
        {totalCartItems === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <ShoppingCartIcon sx={{ fontSize: 40, color: colors.grey[500], opacity: 0.5 }} />
            <Typography>Your cart is empty</Typography>
            <Typography variant="caption" color={colors.grey[500]}>
              Add cards, land deeds, or items from the marketplace to start shopping
            </Typography>
          </Box>
        ) : (
          <>
            <List sx={{ flex: '1 1 auto', overflow: 'auto', minHeight: 0 }}>
              {/* Cards Section */}
              {cartItems.length > 0 && (
                <>
                  <ListItem>
                    <Box display="flex" alignItems="center" gap={1}>
                      <DiamondTwoToneIcon fontSize="small" sx={{ color: colors.greenAccent[500] }} />
                      <Typography variant="subtitle2" fontWeight="bold">
                        Cards ({cartItems.length})
                      </Typography>
                    </Box>
                  </ListItem>
                  <Divider component="li" />
                  
                  {cartItems.map((item, idx) => (
                    <React.Fragment key={item.uid || idx}>
                      <ListItem 
                        secondaryAction={
                          <Tooltip title="Remove from cart">
                            <IconButton 
                              edge="end" 
                              size="small" 
                              onClick={() => handleRemoveItem(item.uid)}
                              color="error"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        }
                        sx={{
                          '&:hover': {
                            bgcolor: theme.palette.mode === 'dark' ? colors.primary[500] : colors.grey[100]
                          }
                        }}
                      >
                        <ListItemText 
                          primary={
                            <Box display="flex" alignItems="center">
                              <Typography variant="body2" fontWeight="bold" noWrap>
                                {item.name || (item.details?.name) || 'Unnamed Card'}
                              </Typography>
                              {getFoilDisplay(item)}
                            </Box>
                          }
                          secondary={
                            // Fix DOM nesting warning - Typography renders as <p> which can't contain <div>
                            <>
                              <Typography variant="caption" component="span" display="block" noWrap color={colors.grey[400]}>
                                ID: {item.uid}
                              </Typography>
                              {(item.market_id || item.market_listing_id) && (
                                <Typography variant="caption" component="span" display="block" noWrap color={colors.blueAccent[400]}>
                                  {item.market_listing_id ? `Listing ID: ${item.market_listing_id}` : `Market ID: ${item.market_id}`}
                                </Typography>
                              )}
                              {/* Display BCX if available */}
                              {item.bcx && (
                                <Typography variant="caption" component="span" display="block" noWrap >
                                  BCX: {Number(item.bcx).toLocaleString('en-US')}
                                </Typography>
                              )}
                              <Box component="span" display="flex" alignItems="center" gap={1}>
                                <Typography variant="caption" component="span" color={colors.greenAccent[400]} fontWeight="bold">
                                  {formatPriceToDec(item.price || convertUsdToDec(item.originalPrice))}
                                </Typography>
                                <Typography variant="caption" component="span" color={colors.blueAccent[400]}>
                                  {formatPriceToUsd(item.originalPrice || (item.price / 1000))}
                                </Typography>
                              </Box>
                            </>
                          }
                        />
                      </ListItem>
                      <Divider component="li" />
                    </React.Fragment>
                  ))}
                </>
              )}
              
              {/* Land Deeds Section */}
              {landCartItems.length > 0 && (
                <>
                  <ListItem>
                    <Box display="flex" alignItems="center" gap={1}>
                      <TerrainIcon fontSize="small" sx={{ color: colors.blueAccent[500] }} />
                      <Typography variant="subtitle2" fontWeight="bold">
                        Land Deeds ({landCartItems.length})
                      </Typography>
                    </Box>
                  </ListItem>
                  <Divider component="li" />
                  
                  {landCartItems.map((item, idx) => (
                    <React.Fragment key={item.uid || idx}>
                      <ListItem 
                        secondaryAction={
                          <Tooltip title="Remove from cart">
                            <IconButton 
                              edge="end" 
                              size="small" 
                              onClick={() => handleRemoveLandItem(item.uid)}
                              color="error"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        }
                        sx={{
                          '&:hover': {
                            bgcolor: theme.palette.mode === 'dark' ? colors.primary[500] : colors.grey[100]
                          },
                          bgcolor: theme.palette.mode === 'dark' ? 'rgba(0, 150, 136, 0.1)' : 'rgba(0, 150, 136, 0.05)'
                        }}
                      >
                        <ListItemText 
                          primary={
                            <Box display="flex" alignItems="center">
                              <Typography variant="body2" fontWeight="bold" noWrap>
                                {item.name || (item.details?.name) || 'Land Deed'}
                              </Typography>
                              <Tooltip title="Land Deed">
                                <Avatar 
                                  sx={{ 
                                    width: 16, 
                                    height: 16, 
                                    ml: 0.5,
                                    bgcolor: colors.blueAccent[300],
                                    color: 'white',
                                    fontSize: '8px',
                                    fontWeight: 'bold'
                                  }}
                                >
                                  LD
                                </Avatar>
                              </Tooltip>
                            </Box>
                          }
                          secondary={
                            <>
                              <Typography variant="caption" component="span" display="block" noWrap color={colors.grey[400]}>
                                ID: {item.uid}
                              </Typography>
                              {(item.market_id || item.market_listing_id) && (
                                <Typography variant="caption" component="span" display="block" noWrap color={colors.blueAccent[400]}>
                                  {item.market_listing_id ? `Listing ID: ${item.market_listing_id}` : `Market ID: ${item.market_id}`}
                                </Typography>
                              )}
                              {item.region && (
                                <Typography variant="caption" component="span" display="block" noWrap color={colors.grey[400]}>
                                  Region: {item.region}
                                </Typography>
                              )}
                              <Box component="span" display="flex" alignItems="center" gap={1}>
                                <Typography variant="caption" component="span" color={colors.greenAccent[400]} fontWeight="bold">
                                  {formatPriceToDec(convertUsdToDec(item.price || item.buy_price || item.listing_price || 0))}
                                </Typography>
                                <Typography variant="caption" component="span" color={colors.blueAccent[400]}>
                                  {formatPriceToUsd(item.price || item.buy_price || item.listing_price || 0)}
                                </Typography>
                              </Box>
                            </>
                          }
                        />
                      </ListItem>
                      <Divider component="li" />
                    </React.Fragment>
                  ))}
                </>
              )}

              {/* Market Items Section (packs, totems, resources, ...) */}
              {itemCartItems.length > 0 && (
                <>
                  <ListItem>
                    <Box display="flex" alignItems="center" gap={1}>
                      <StyleIcon fontSize="small" sx={{ color: colors.blueAccent[500] }} />
                      <Typography variant="subtitle2" fontWeight="bold">
                        Items ({itemCartItems.length})
                      </Typography>
                    </Box>
                  </ListItem>
                  <Divider component="li" />

                  {itemCartItems.map((cartItem) => (
                    <React.Fragment key={cartItem.id}>
                      <ListItem
                        secondaryAction={
                          <Tooltip title="Remove from cart">
                            <IconButton
                              edge="end"
                              size="small"
                              onClick={() => handleRemoveItemCartEntry(cartItem.id)}
                              color="error"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        }
                        sx={{
                          '&:hover': {
                            bgcolor: theme.palette.mode === 'dark' ? colors.primary[500] : colors.grey[100]
                          }
                        }}
                      >
                        <ListItemText
                          primary={
                            <Typography variant="body2" fontWeight="bold" noWrap>
                              {cartItem.quantity}x {cartItem.item?.detailName || 'Item'}
                            </Typography>
                          }
                          secondary={
                            <>
                              {cartItem.listings?.length > 0 && (
                                <Typography variant="caption" component="span" display="block" noWrap color={colors.grey[400]}>
                                  {cartItem.listings.length === 1
                                    ? `Seller: ${cartItem.listings[0].seller}`
                                    : `${cartItem.listings.length} sellers`}
                                </Typography>
                              )}
                              <Box component="span" display="flex" alignItems="center" gap={1}>
                                <Typography variant="caption" component="span" color={colors.greenAccent[400]} fontWeight="bold">
                                  {formatPriceToDec(convertUsdToDec(cartItem.totalCost || 0))}
                                </Typography>
                                <Typography variant="caption" component="span" color={colors.blueAccent[400]}>
                                  {formatPriceToUsd(cartItem.totalCost || 0)}
                                </Typography>
                              </Box>
                            </>
                          }
                        />
                      </ListItem>
                      <Divider component="li" />
                    </React.Fragment>
                  ))}
                </>
              )}
            </List>

            <Paper
              elevation={0}
              sx={{
                p: 1.5,
                bgcolor: theme.palette.mode === 'dark' ? colors.primary[500] : colors.grey[100],
                borderRadius: 0,
                flexShrink: 0
              }}
            >
              <Box>
                <Typography variant="subtitle2" fontWeight="bold">
                  Cart Summary
                </Typography>
                <Box display="flex" justifyContent="space-between" alignItems="center" mt={0.5}>
                  <Typography variant="body2">Total (USD):</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {formatPriceToUsd(totals.usdTotal)}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color={colors.greenAccent[500]}>Total (DEC):</Typography>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Typography variant="body2" fontWeight="bold" color={colors.greenAccent[500]}>
                      {formatPriceToDec(totals.decTotal)}
                    </Typography>
                    <Avatar 
                      src="/assets/img_dec_256.png" 
                      alt="DEC" 
                      sx={{ width: 16, height: 16 }} 
                    />
                  </Box>
                </Box>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="#FFD700">Total (Credits):</Typography>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Typography variant="body2" fontWeight="bold" color="#FFD700">
                      {formatPriceToCredits(totals.creditsTotal)}
                    </Typography>
                    <Avatar 
                      sx={{ 
                        width: 16, 
                        height: 16,
                        bgcolor: '#FFD700',
                        color: 'black',
                        fontSize: '8px',
                        fontWeight: 'bold'
                      }}
                    >$</Avatar>
                  </Box>
                </Box>
                {/* Show BCX total only if there are cards with BCX */}
                {totals.bcxTotal > 0 && (
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color={colors.blueAccent[400]}>Total (BCX):</Typography>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Typography variant="body2" fontWeight="bold" color={colors.blueAccent[400]}>
                        {Number(totals.bcxTotal).toLocaleString('en-US')}
                      </Typography>
                      <Tooltip title="Battle Collection eXperience - Power value of cards">
                        <Avatar 
                          sx={{ 
                            width: 16, 
                            height: 16,
                            bgcolor: colors.blueAccent[500],
                            color: 'white',
                            fontSize: '7px',
                            fontWeight: 'bold'
                          }}
                        >BCX</Avatar>
                      </Tooltip>
                    </Box>
                  </Box>
                )}
              </Box>
              
              <Box mt={1.5}>
                {/* Clear Cart Buttons */}
                <Box display="flex" justifyContent="space-between" gap={1} mb={1}>
                  {cartItems.length > 0 && (
                    <Button 
                      variant="outlined" 
                      color="error" 
                      size="small" 
                      startIcon={<DeleteIcon />}
                      onClick={clearCart}
                      sx={{ flex: 1 }}
                    >
                      Clear Cards
                    </Button>
                  )}
                  
                  {landCartItems.length > 0 && (
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      startIcon={<DeleteIcon />}
                      onClick={clearLandCart}
                      sx={{ flex: 1 }}
                    >
                      Clear Land Deeds
                    </Button>
                  )}

                  {itemCartItems.length > 0 && (
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      startIcon={<DeleteIcon />}
                      onClick={clearItemCart}
                      sx={{ flex: 1 }}
                    >
                      Clear Items
                    </Button>
                  )}
                </Box>

                {/* Card Purchase Section */}
                {cartItems.length > 0 && (
                  <>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <DiamondTwoToneIcon fontSize="small" sx={{ color: colors.greenAccent[500] }} />
                      Buy all cards with:
                    </Typography>
                    
                    <Box display="flex" gap={1} mb={2}>
                      <Tooltip title={`Buy all cards for ${formatPriceToDec(totals.decTotal)}`}>
                        <Button 
                          variant="contained" 
                          size="small" 
                          fullWidth
                          disabled={loading}
                          onClick={() => handleBuyAllItems('DEC')}
                          sx={{
                            bgcolor: colors.greenAccent[500],
                            color: 'black',
                            fontWeight: 'bold',
                            '&:hover': {
                              bgcolor: colors.greenAccent[700],
                            }
                          }}
                        >
                          <Box display="flex" alignItems="center" gap={0.5}>
                            {loading ? <CircularProgress size={16} color="inherit" /> : (
                              <>
                                <Typography variant="body2" fontWeight="bold">DEC</Typography>
                                <Avatar 
                                  src="/assets/img_dec_256.png" 
                                  alt="DEC" 
                                  sx={{ width: 16, height: 16 }} 
                                />
                              </>
                            )}
                          </Box>
                        </Button>
                      </Tooltip>
                      
                      <Tooltip title={`Buy all cards for ${formatPriceToCredits(totals.creditsTotal)} (1000 Credits = $1)`}>
                        <Button 
                          variant="contained" 
                          size="small" 
                          fullWidth
                          disabled={loading}
                          onClick={() => handleBuyAllItems('CREDITS')}
                          sx={{
                            bgcolor: colors.blueAccent[500],
                            color: theme.palette.mode === 'dark' ? 'white' : 'black',
                            fontWeight: 'bold',
                            '&:hover': {
                              bgcolor: colors.blueAccent[700],
                            }
                          }}
                        >
                          <Box display="flex" alignItems="center" gap={0.5}>
                            {loading ? <CircularProgress size={16} color="inherit" /> : (
                              <>
                                <Typography variant="body2" fontWeight="bold">Credits</Typography>
                                <Avatar 
                                  sx={{ 
                                    width: 16, 
                                    height: 16,
                                    bgcolor: '#FFD700',
                                    color: 'black',
                                    fontSize: '10px',
                                    fontWeight: 'bold'
                                  }}
                                >$</Avatar>
                              </>
                            )}
                          </Box>
                        </Button>
                      </Tooltip>
                    </Box>
                  </>
                )}
                
                {/* Land Purchase Section */}
                {landCartItems.length > 0 && (
                  <>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <TerrainIcon fontSize="small" sx={{ color: colors.blueAccent[500] }} />
                      Buy all land deeds with:
                    </Typography>
                    
                    <Box display="flex" gap={1}>
                      <Tooltip title={`Buy all land deeds for ${formatPriceToDec(convertUsdToDec(landCartItems.reduce((sum, item) => sum + parseFloat(item.price || item.buy_price || 0), 0)))}`}>
                        <Button 
                          variant="contained" 
                          size="small" 
                          fullWidth
                          disabled={landLoading}
                          onClick={() => handleBuyAllLandItems('DEC')}
                          sx={{
                            bgcolor: colors.greenAccent[500],
                            color: 'black',
                            fontWeight: 'bold',
                            '&:hover': {
                              bgcolor: colors.greenAccent[700],
                            }
                          }}
                        >
                          <Box display="flex" alignItems="center" gap={0.5}>
                            {landLoading ? <CircularProgress size={16} color="inherit" /> : (
                              <>
                                <Typography variant="body2" fontWeight="bold">DEC</Typography>
                                <Avatar 
                                  src="/assets/img_dec_256.png" 
                                  alt="DEC" 
                                  sx={{ width: 16, height: 16 }} 
                                />
                              </>
                            )}
                          </Box>
                        </Button>
                      </Tooltip>
                      
                      <Tooltip title={`Buy all land deeds for ${formatPriceToCredits(convertUsdToCredits(landCartItems.reduce((sum, item) => sum + parseFloat(item.price || item.buy_price || 0), 0)))} (1000 Credits = $1)`}>
                        <Button 
                          variant="contained" 
                          size="small" 
                          fullWidth
                          disabled={landLoading}
                          onClick={() => handleBuyAllLandItems('CREDITS')}
                          sx={{
                            bgcolor: colors.blueAccent[500],
                            color: theme.palette.mode === 'dark' ? 'white' : 'black',
                            fontWeight: 'bold',
                            '&:hover': {
                              bgcolor: colors.blueAccent[700],
                            }
                          }}
                        >
                          <Box display="flex" alignItems="center" gap={0.5}>
                            {landLoading ? <CircularProgress size={16} color="inherit" /> : (
                              <>
                                <Typography variant="body2" fontWeight="bold">Credits</Typography>
                                <Avatar 
                                  sx={{ 
                                    width: 16, 
                                    height: 16,
                                    bgcolor: '#FFD700',
                                    color: 'black',
                                    fontSize: '10px',
                                    fontWeight: 'bold'
                                  }}
                                >$</Avatar>
                              </>
                            )}
                          </Box>
                        </Button>
                      </Tooltip>
                    </Box>
                  </>
                )}

                {/* Items Purchase Section */}
                {itemCartItems.length > 0 && (
                  <>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <StyleIcon fontSize="small" sx={{ color: colors.blueAccent[500] }} />
                      Buy all items with:
                    </Typography>

                    <Box display="flex" gap={1}>
                      <Tooltip title={`Buy all items for ${formatPriceToDec(convertUsdToDec(itemCartItems.reduce((sum, item) => sum + parseFloat(item.totalCost || 0), 0)))}`}>
                        <Button
                          variant="contained"
                          size="small"
                          fullWidth
                          disabled={itemLoading}
                          onClick={() => handleBuyAllItemCartEntries('DEC')}
                          sx={{
                            bgcolor: colors.greenAccent[500],
                            color: 'black',
                            fontWeight: 'bold',
                            '&:hover': {
                              bgcolor: colors.greenAccent[700],
                            }
                          }}
                        >
                          <Box display="flex" alignItems="center" gap={0.5}>
                            {itemLoading ? <CircularProgress size={16} color="inherit" /> : (
                              <>
                                <Typography variant="body2" fontWeight="bold">DEC</Typography>
                                <Avatar
                                  src="/assets/img_dec_256.png"
                                  alt="DEC"
                                  sx={{ width: 16, height: 16 }}
                                />
                              </>
                            )}
                          </Box>
                        </Button>
                      </Tooltip>

                      <Tooltip title={`Buy all items for ${formatPriceToCredits(convertUsdToCredits(itemCartItems.reduce((sum, item) => sum + parseFloat(item.totalCost || 0), 0)))} (1000 Credits = $1)`}>
                        <Button
                          variant="contained"
                          size="small"
                          fullWidth
                          disabled={itemLoading}
                          onClick={() => handleBuyAllItemCartEntries('CREDITS')}
                          sx={{
                            bgcolor: colors.blueAccent[500],
                            color: theme.palette.mode === 'dark' ? 'white' : 'black',
                            fontWeight: 'bold',
                            '&:hover': {
                              bgcolor: colors.blueAccent[700],
                            }
                          }}
                        >
                          <Box display="flex" alignItems="center" gap={0.5}>
                            {itemLoading ? <CircularProgress size={16} color="inherit" /> : (
                              <>
                                <Typography variant="body2" fontWeight="bold">Credits</Typography>
                                <Avatar
                                  sx={{
                                    width: 16,
                                    height: 16,
                                    bgcolor: '#FFD700',
                                    color: 'black',
                                    fontSize: '10px',
                                    fontWeight: 'bold'
                                  }}
                                >$</Avatar>
                              </>
                            )}
                          </Box>
                        </Button>
                      </Tooltip>
                    </Box>
                  </>
                )}
              </Box>
            </Paper>
          </>
        )}
      </Popover>
      
      {/* Diamond Animation Component */}
      {animationTrigger > 0 && (
        <DiamondAnimation 
          key={animationTrigger} // Force re-render on new animation
          trigger={animationTrigger}
          sourceElement={cartRef.current}
          targetElement={null} // Will be resolved inside DiamondAnimation
          amount={totals.decTotal?.toString() || "1.000"}
          diamondCount={Math.min(totalCartItems || 1, 8)} // Scale diamonds with total items, max 8
          onAnimationComplete={() => {
            console.log('Shopping animation completed!');
            setAnimationTrigger(0); // Reset trigger after animation
          }}
          // Pass custom target resolver function
          targetResolver={getCashbackElement}
        />
      )}
    </React.Fragment>
  );
};

export default ShoppingCard;
